// @flow
import type {Node} from 'react'
import React, {Fragment, useCallback} from 'react'
import {useFragment, graphql} from 'react-relay'
import {UncontrolledTooltip} from 'reactstrap'
import classNames from 'classnames'
import {DragDropContext, Droppable} from 'react-beautiful-dnd'

import liftNodes from 'utils/GraphQL/typedLiftNodes'
import usePersistentState from 'utils/hooks/usePersistentState'
import LocalStorageCell from 'utils/LocalStorageCell'
import DraggableItem from 'components/ui/DraggableItem'
import DropdownSection from 'components/SideBar/ui/DropdownSection'

import styles from './index.scss'
import type {
  OrgSelect_organization$key as OrganizationKey,
} from './__generated__/OrgSelect_organization.graphql'
import type {
  OrgSelect_viewer$key as ViewerKey,
} from './__generated__/OrgSelect_viewer.graphql'

type Props = $ReadOnly<{
  organization: OrganizationKey,
  viewer: ViewerKey,
  sideBarExpanded: boolean,
  toggleSidebar: () => void,
}>

const organizationFragment = graphql`
  fragment OrgSelect_organization on Organization {
    databaseId
    name
    iconUrl
    resourcePath
  }
`

const viewerFragment = graphql`
  fragment OrgSelect_viewer on Person {
    id
    organizations {
      edges {
        node {
          databaseId
          name
          iconUrl
          resourcePath
        }
      }
    }
  }
`

function OrgSelect({
  organization: organizationKey,
  viewer: viewerKey,
  sideBarExpanded,
  toggleSidebar,
}: Props): Node {
  const selectedButtonClass = classNames(
    styles.iconButton,
    styles.selectedIconButton,
    styles.withArrow,
    {
      [styles.withArrowClosed]: !sideBarExpanded,
    },
  )

  const buttonClass = classNames('btn', styles.iconButton, styles.filteredIconButton)
  const filteredIconClass = classNames(styles.orgAvatar, styles.filteredOrgAvatar)
  const selectedOrg = useFragment(organizationFragment, organizationKey)
  const viewer = useFragment(viewerFragment, viewerKey)
  const viewerOrganizations = liftNodes(viewer.organizations).filter(Boolean)
  const isSelectedOrgInViewerOrgs = viewerOrganizations
    .some((org) => org.databaseId === selectedOrg.databaseId)
  const storageKey = `org-list-${viewer.id}`

  const storedOrgs = new LocalStorageCell(storageKey).get() || []
  const initialViewerOrgs = isSelectedOrgInViewerOrgs
    ? viewerOrganizations : [...viewerOrganizations, selectedOrg]

  const initialState = () => {
    const seenOrganizations = storedOrgs.filter((storedOrg) => (
      initialViewerOrgs.some((org) => org.databaseId === storedOrg.databaseId)
    ))

    const notSeenOrganizations = initialViewerOrgs.filter((initialOrg) => (
      !seenOrganizations.some((org) => org.databaseId === initialOrg.databaseId)
    ))

    return [...seenOrganizations, ...notSeenOrganizations]
  }

  const [organizations, setOrganizations] = usePersistentState(
    initialState(),
    storageKey,
    () => false,
  )

  const switchOrg = useCallback((organization) => {
    window.location.href = organization.resourcePath
  }, [])

  const inTooManyOrgs = organizations.length > 5

  const renderOrgImage = (organization, iconStyle, showTooltip) => {
    const toolTipDelay = inTooManyOrgs ? {show: 1000, hide: 0} : {show: 200, hide: 0}
    return (
      <Fragment>
        <img
          className={iconStyle}
          src={organization.iconUrl}
          alt={organization.name}
        />
        {showTooltip && (
          <UncontrolledTooltip
            className={styles.tooltip}
            placement="right"
            target={`organization_${organization.databaseId}`}
            delay={toolTipDelay}
          >
            {organization.name}
          </UncontrolledTooltip>
        )}
      </Fragment>
    )
  }

  const renderOrgButton = (organization, withName, props) => {
    const isSelectedOrg = organization.databaseId === selectedOrg.databaseId
    const theButtonClass = classNames(
      {
        [buttonClass]: !isSelectedOrg,
        [selectedButtonClass]: isSelectedOrg,
        [styles.selectedButtonStaticBlock]: isSelectedOrg && inTooManyOrgs && !withName,
      },
    )
    let onClick = isSelectedOrg ? toggleSidebar : () => switchOrg(organization)
    onClick = inTooManyOrgs && isSelectedOrg ? null : onClick

    return (
      <button
        {...props}
        id={`organization_${organization.databaseId}`}
        className={theButtonClass}
        type="button"
        onClick={onClick}
      >
        {renderOrgImage(organization, filteredIconClass, !withName)}
        {withName && <div className={styles.orgName}>{organization.name}</div>}
      </button>
    )
  }

  const renderDraggableOrg = (organization, index, isExpanded) => {
    const isSelectedOrg = organization.databaseId === selectedOrg.databaseId

    return (
      <DraggableItem
        key={`org-${organization.databaseId}`}
        draggableId={organization.databaseId}
        index={index}
        disableInteractiveElementBlocking={isSelectedOrg}
      >
        {(props) => (
          renderOrgButton(organization, isExpanded, {...props})
        )}
      </DraggableItem>
    )
  }

  const reorderOrgs = (startIndex, endIndex) => {
    const targetOrg = organizations[startIndex]

    if (endIndex > startIndex) {
      return [
        ...organizations.slice(0, startIndex),
        ...organizations.slice(startIndex + 1, endIndex + 1),
        targetOrg,
        ...organizations.slice(endIndex + 1),
      ]
    }

    return [
      ...organizations.slice(0, endIndex),
      targetOrg,
      ...organizations.slice(endIndex, startIndex),
      ...organizations.slice(startIndex + 1),
    ]
  }

  const onDragEnd = (result) => {
    if (!result.destination)
      return

    const items = reorderOrgs(result.source.index, result.destination.index)

    setOrganizations(items)
  }

  const iconsContainerClass = classNames(styles.iconsContainer, {
    [styles.iconsContainerExpanded]: inTooManyOrgs,
  })
  const renderReordableOrgList = (isExpanded) => (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppableOrgs">
        {(provided) => (
          <div
            className={iconsContainerClass}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {organizations.map((org, index) => renderDraggableOrg(org, index, isExpanded))}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )

  return (
    <Fragment>
      {!inTooManyOrgs && renderReordableOrgList(false)}

      {inTooManyOrgs && (
        <DropdownSection
          title=""
          className={styles.orgSelectDropdownSection}
          popoverClassName={styles.orgSelectDropdownPopover}
          renderButton={() => renderOrgButton(selectedOrg, false, {})}
          sectionName="org_select_dropdown"
          renderChildren={() => (
            <Fragment>
              {renderReordableOrgList(true)}
            </Fragment>
          )}
        />
      )}
    </Fragment>
  )
}

export default OrgSelect
